// @ts-check
// Chamadas do menu personalizável, dos primeiros passos e do CEP (contrato 08-contrato-navegacao-mobile.md), com o
// estado local do bootstrap atualizado sem recarregar a página.
import { signal } from '@preact/signals';
import { api } from './api-client.js';
import { bootstrapState } from './metadata-store.js';

/** @param {any} body @param {string} key */
const readData = (body, key) => body?.data?.[key] ?? body?.data ?? body;

/** @param {(items: import('./metadata-store.js').NavigationMenuItem[]) => import('./metadata-store.js').NavigationMenuItem[]} update */
function updateLocalItems(update) {
  const bootstrap = bootstrapState.value;
  if (bootstrap) bootstrapState.value = { ...bootstrap, navigationMenuItems: update(bootstrap.navigationMenuItems) };
}

/**
 * @param {{ scope?: 'MEMBER' | 'WORKSPACE', type: 'VIEW' | 'LINK' | 'FOLDER', viewId?: string, link?: string, name?: string, icon?: string, section?: string }} input
 */
export async function createNavigationItem(input) {
  const item = readData(await api.post('/rest/metadata/navigation-items', input), 'createNavigationItem');
  if (item?.id) updateLocalItems((items) => [...items.filter((candidate) => candidate.id !== item.id), item]);
  return item;
}

/** @param {string} itemId @param {{ name?: string | null, icon?: string, section?: string, folderId?: string | null, link?: string }} patch */
export async function updateNavigationItem(itemId, patch) {
  const item = readData(await api.patch(`/rest/metadata/navigation-items/${itemId}`, patch), 'updateNavigationItem');
  if (item?.id) updateLocalItems((items) => items.map((candidate) => (candidate.id === item.id ? { ...candidate, ...item } : candidate)));
  return item;
}

/** Remove o item; uma pasta removida solta os favoritos de dentro. @param {string} itemId */
export async function deleteNavigationItem(itemId) {
  await api.delete(`/rest/metadata/navigation-items/${itemId}`);
  updateLocalItems((items) => items
    .filter((item) => item.id !== itemId)
    .map((item) => (item.folderId === itemId ? { ...item, folderId: null } : item)));
}

/** Padrão da escola (flag WORKSPACE). @param {{ items: Array<{ id: string, section?: string, isHidden?: boolean }> }} body */
export async function saveWorkspaceNavigation(body) {
  const result = readData(await api.put('/rest/metadata/navigation/workspace', body), 'updateWorkspaceNavigation');
  const items = result?.navigationMenuItems;
  const bootstrap = bootstrapState.value;
  if (bootstrap && Array.isArray(items)) bootstrapState.value = { ...bootstrap, navigationMenuItems: items };
}

/** Menu do membro; `reset` volta ao padrão da escola. @param {{ order?: string[], hiddenIds?: string[], reset?: boolean }} body */
export async function saveMemberNavigation(body) {
  const layout = readData(await api.put('/rest/metadata/navigation/me', body), 'navigationLayout');
  const bootstrap = bootstrapState.value;
  if (bootstrap) bootstrapState.value = { ...bootstrap, navigationLayout: layout ?? { order: [], hiddenIds: [] } };
}

// Primeiros passos

/**
 * @typedef {{ key: string, isDone: boolean, isSkipped: boolean }} OnboardingStep
 * @typedef {{ isVisible: boolean, steps: OnboardingStep[], completedCount: number, totalCount: number }} OnboardingStatus
 */

/** @type {import('@preact/signals').Signal<OnboardingStatus | null>} */
export const onboardingState = signal(null);

let lastOnboardingLoad = 0;
// a checklist confere o progresso ao trocar de tela, no máximo a cada 5 segundos
const ONBOARDING_RELOAD_INTERVAL_MILLISECONDS = 5000;

/** @param {{ force?: boolean }} [options] */
export async function loadOnboarding({ force = false } = {}) {
  const now = Date.now();
  if (!force && now - lastOnboardingLoad < ONBOARDING_RELOAD_INTERVAL_MILLISECONDS) return onboardingState.value;
  lastOnboardingLoad = now;
  try {
    onboardingState.value = readData(await api.get('/rest/onboarding'), 'onboarding');
  } catch {
    // sem checklist não há o que mostrar; a tela segue normal
    onboardingState.value = null;
  }
  return onboardingState.value;
}

/** @param {{ skipStep?: string, dismiss?: boolean }} input */
export async function updateOnboarding(input) {
  onboardingState.value = readData(await api.post('/rest/onboarding', input), 'onboarding');
}

// CEP

/**
 * @typedef {{ postcode: string, street: string, neighborhood: string, city: string, state: string, country: string }} PostcodeAddress
 */

/** @param {string} digits @returns {Promise<PostcodeAddress>} */
export async function lookupPostcode(digits) {
  return readData(await api.get(`/rest/address/postcodes/${digits}`), 'address');
}
